// Legal document generation wizard — PDN / DN / LBA / SD2 (Eng & Scot).
// Step 1 Details & parties → Step 2 Amounts → Step 3 Dates → Step 4 Review & generate.
import { useState, useEffect, useRef, useCallback } from "react";
import { format, parse, isValid } from "date-fns";
import {
  FileSignature, FileText, Loader2, CheckCircle2, XCircle, Plus,
  Search, MapPin, CalendarIcon, Building2, ChevronLeft, ChevronRight, Landmark,
  Sparkles, Minus, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";
import { cn, ukDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  getLegalDocPrefill, generateLegalDocs, searchCompaniesForDocs,
  getCompanyProfileForDocs, getLegalDocAddressBook, aiFillLegalDoc, discoverLegalGuarantors, setLegalChosenCourt,
  postcodeAutocomplete, postcodeResolve, searchCourtsByPostcode,
} from "@/lib/api";
import { ServicePackDialog } from "./ServicePackDialog";
import GateChecklistDialog from "@/pages/legal/GateChecklistDialog";
import { useAuth } from "@/contexts/AuthContext";
import { cur } from "@/lib/regionFmt";

// How picking a CH company / address fills fields, per doc kind
const KIND_CONFIG = {
  pdn: {
    companyFill: { name: ["BorrowerBusinessName", "BorrowerCompany"] },
    addressFill: { line: "BorrowerAddress", city: "BorrowerCity", postcode: "BorrowerZipcode" },
  },
  dn: {
    companyFill: { name: ["BorrowerCompanyName"], address: "CompanyAddress" },
    addressFill: { full: "CompanyAddress" },
  },
  lba: {
    companyFill: { name: ["BorrowerCompanyName"], number: "BorrowerCompanyNumber", address: "BorrowerCompanyAddress" },
    addressFill: { full: "BorrowerCompanyAddress" },
  },
  sd2: {},
  sd_scot: {},
};

const STEP_NAMES = { party: "Details & parties", amount: "Amounts", date: "Dates", review: "Review & generate" };
const STEP_ORDER = ["court", "party", "amount", "service", "options", "date"];

const SOURCE_BADGE = {
  loandisk:        { text: "LD ✓", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  companies_house: { text: "CH ✓", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  computed:        { text: "auto", cls: "bg-slate-50 text-slate-500 border-slate-200" },
  case:            { text: "file ✓", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  ai:              { text: "AI ✨", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  picked:          { text: "chosen", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};
const ukPostcode = (text) => ((text || "").toUpperCase().match(/\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/) || [])[1] || "";

const parseMoney = (v) => {
  const f = parseFloat(String(v ?? "").replace(/[£$,\s]/g, ""));
  return Number.isFinite(f) ? f : null;
};
const fmtMoney = (f) => f.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Scottish postcode areas — jurisdiction guard for statutory demands.
const SCOT_AREAS = new Set(["AB", "DD", "DG", "EH", "FK", "G", "HS", "IV", "KA", "KW", "KY", "ML", "PA", "PH", "TD", "ZE"]);
export const postcodeCountry = (address) => {
  const m = String(address || "").toUpperCase().match(/\b([A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2})\b/);
  if (!m) return null;
  const area = m[1].match(/^[A-Z]{1,2}/)[0];
  return SCOT_AREAS.has(area) ? "scotland" : "england_wales";
};

function SourceBadge({ source }) {
  const b = SOURCE_BADGE[source];
  if (!b) return null;
  return <Badge variant="outline" className={cn("text-[9px] px-1 py-0 ml-1.5 font-normal", b.cls)}>{b.text}</Badge>;
}

// ─── Date picker input (editable text + calendar popover) ──────────────────
function DateField({ value, onChange, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const parsed = value ? parse(value, "dd/MM/yyyy", new Date()) : null;
  return (
    <div className="flex gap-1">
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "DD/MM/YYYY"}
        className="h-8 text-xs flex-1"
        data-testid={testId}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 w-8 p-0 shrink-0" data-testid={`${testId}-picker`}>
            <CalendarIcon className="w-3.5 h-3.5 text-slate-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 z-[70]" align="end">
          <Calendar
            mode="single"
            selected={parsed && isValid(parsed) ? parsed : undefined}
            defaultMonth={parsed && isValid(parsed) ? parsed : new Date()}
            onSelect={(d) => { if (d) { onChange(format(d, "dd/MM/yyyy")); setOpen(false); } }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ─── Companies House search-as-you-type ────────────────────────────────────
function CompanySearchField({ value, onChange, onPickCompany, placeholder, testId }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  const doSearch = useCallback((q) => {
    clearTimeout(timer.current);
    if (!q || q.trim().length < 3) { setResults([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await searchCompaniesForDocs(q.trim());
        setResults(r.items || []);
        setOpen((r.items || []).length > 0);
      } catch (e) {
        setResults([]);
        // One toast for the whole typing session: this runs on every debounced keystroke.
        toast.error(errorDetailMessage(e, "Companies House search failed"), { id: "ch-search-failed" });
      }
      finally { setSearching(false); }
    }, 400);
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <Input
          value={value ?? ""}
          onChange={(e) => { onChange(e.target.value); doSearch(e.target.value); }}
          onBlur={() => setTimeout(() => setOpen(false), 250)}
          placeholder={placeholder}
          className="h-8 text-xs pr-7"
          data-testid={testId}
        />
        {searching
          ? <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-2 top-2.5 text-slate-400" />
          : <Building2 className="w-3.5 h-3.5 absolute right-2 top-2.5 text-slate-400" />}
      </div>
      {open && (
        <div className="absolute z-[80] mt-1 w-full bg-white border rounded-md shadow-lg max-h-52 overflow-y-auto" data-testid={`${testId}-results`}>
          {results.map((c, i) => (
            <button
              key={i}
              type="button"
              className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 border-b last:border-b-0"
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); onPickCompany(c); }}
            >
              <div className="font-medium">{c.name} <span className="text-[10px] text-muted-foreground">#{c.number} · {c.status}</span></div>
              <div className="text-[10px] text-muted-foreground truncate">{c.address}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Address finder popover (on-file addresses + Ideal Postcodes lookup) ───
export function AddressFinder({ addressBook, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const search = async () => {
    if (!term.trim()) return;
    setSearching(true); setError(null);
    try {
      const r = await postcodeAutocomplete(term.trim());
      if (!r.ok) setError(r.error);
      setSuggestions(r.suggestions || []);
    } catch (e) { setError(e.message); }
    finally { setSearching(false); }
  };

  const resolve = async (id) => {
    try {
      const r = await postcodeResolve(id);
      if (r.ok) { onPick(r); setOpen(false); }
      else toast.error(r.error || "Could not resolve address");
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 px-2 shrink-0 text-[11px]" data-testid={testId}>
          <MapPin className="w-3 h-3 mr-1 text-slate-500" />Find
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-2.5 z-[70]" align="end">
        <div className="space-y-2">
          {(addressBook || []).length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">On file</div>
              <div className="space-y-0.5 max-h-32 overflow-y-auto">
                {addressBook.map((a, i) => (
                  <button
                    key={i} type="button"
                    className="w-full text-left px-2 py-1 text-[11px] rounded hover:bg-emerald-50 border border-transparent hover:border-emerald-200"
                    onClick={() => { onPick(a); setOpen(false); }}
                    data-testid={`${testId}-onfile-${i}`}
                  >
                    <div className="font-medium text-[10px] text-slate-500">{a.label}</div>
                    <div className="truncate">{a.full}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Postcode / address search</div>
            <div className="flex gap-1">
              <Input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="e.g. SW1A 2AA or 10 Downing St"
                className="h-7 text-xs"
                data-testid={`${testId}-term`}
              />
              <Button size="sm" className="h-7 px-2" onClick={search} disabled={searching} data-testid={`${testId}-search`}>
                {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              </Button>
            </div>
            {error && <div className="text-[10px] text-amber-700 mt-1">{error}</div>}
            {suggestions.length > 0 && (
              <div className="space-y-0.5 max-h-40 overflow-y-auto mt-1">
                {suggestions.map((s) => (
                  <button
                    key={s.id} type="button"
                    className="w-full text-left px-2 py-1 text-[11px] rounded hover:bg-blue-50 border border-transparent hover:border-blue-200"
                    onClick={() => resolve(s.id)}
                  >
                    {s.address}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Court picker (editable input + nearby-court suggestions popover) ──────
// Court letters: the court is auto-populated; "Edit court" opens this inline
// postcode search + dropdown. No free-typing confusion.
function CourtEditor({ options, defaultTerm, onPick, onCancel, testId }) {
  const [term, setTerm] = useState(defaultTerm || "");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [choice, setChoice] = useState("");
  const list = results ?? (options || []);
  const courtAddress = (c) => {
    if (typeof c.address === "string") return c.address;
    const a = c.address || {};
    return [...(a.lines || []), a.town, a.postcode].filter(Boolean).join(", ");
  };
  const search = async () => {
    if (!term.trim()) return;
    setSearching(true); setError(null); setChoice("");
    try {
      const r = await searchCourtsByPostcode(term.trim(), "Bankruptcy", 8);
      if (r.success === false) { setError(r.error || "No courts found"); setResults([]); }
      else setResults(r.courts || []);
    } catch (e) { setError(e?.response?.data?.detail || e.message); setResults([]); }
    finally { setSearching(false); }
  };
  useEffect(() => { if (defaultTerm && !(options || []).length) search(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const use = () => {
    const c = list[Number(choice)];
    if (c) onPick({ name: c.name, address: courtAddress(c) });
  };
  return (
    <div className="border rounded-md p-3 bg-slate-50 space-y-2" data-testid={`${testId}-editor`}>
      <div className="text-xs font-medium text-slate-700">Find a different court</div>
      <div className="flex gap-2">
        <Input value={term} onChange={(e) => setTerm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Postcode, e.g. DA12 5PG" className="h-9 text-sm flex-1" data-testid={`${testId}-search-term`} />
        <Button className="h-9" onClick={search} disabled={searching || !term.trim()} data-testid={`${testId}-search-btn`}>
          {searching ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Search
        </Button>
      </div>
      {error && <div className="text-xs text-rose-700">{error}</div>}
      <select value={choice} onChange={(e) => setChoice(e.target.value)} className="h-9 text-sm border rounded px-2 w-full bg-white"
        data-testid={`${testId}-results`} disabled={!list.length}>
        <option value="">{list.length ? `Choose a court (${list.length} nearest)…` : searching ? "Searching…" : "Search a postcode to list courts"}</option>
        {list.map((c, i) => (
          <option key={i} value={i}>
            {c.name}{c.town || c.address?.town ? ` — ${c.town || c.address?.town}` : ""}{c.distance_miles != null ? ` (${c.distance_miles} mi)` : ""}
          </option>
        ))}
      </select>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={onCancel} data-testid={`${testId}-cancel`}>Cancel</Button>
        <Button size="sm" className="h-8 text-xs" onClick={use} disabled={choice === ""} data-testid={`${testId}-use`}>Use this court</Button>
      </div>
    </div>
  );
}

function CourtPickerField({ value, onChange, options, placeholder, testId, onPickCourt, defaultTerm = "", autoOpen = false }) {
  const [open, setOpen] = useState(autoOpen);
  const [term, setTerm] = useState(defaultTerm);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const search = async () => {
    if (!term.trim()) return;
    setSearching(true); setError(null);
    try {
      const r = await searchCourtsByPostcode(term.trim(), "Bankruptcy", 5);
      if (r.success === false) { setError(r.error || "No courts found"); setResults([]); }
      else setResults(r.courts || []);
    } catch (e) { setError(e?.response?.data?.detail || e.message); setResults([]); }
    finally { setSearching(false); }
  };

  const courtFull = (c) => {
    if (c.full) return c.full;
    const a = c.address || {};
    return [c.name, ...(a.lines || []), a.town, a.postcode].filter(Boolean).join(", ");
  };
  const courtAddress = (c) => {
    if (typeof c.address === "string") return c.address;
    const a = c.address || {};
    return [...(a.lines || []), a.town, a.postcode].filter(Boolean).join(", ");
  };
  const pick = (c) => {
    if (onPickCourt) onPickCourt({ name: c.name, address: courtAddress(c), full: courtFull(c) });
    else onChange(courtFull(c));
    setOpen(false);
  };

  const CourtRow = ({ c, idx, prefix }) => (
    <button
      key={idx} type="button"
      className="w-full text-left px-2 py-1 text-[11px] rounded hover:bg-blue-50 border border-transparent hover:border-blue-200"
      onClick={() => pick(c)}
      data-testid={`${testId}-${prefix}-${idx}`}
    >
      <div className="font-medium">{c.name}</div>
      <div className="text-[10px] text-muted-foreground">
        {[c.town || (c.address?.town), c.distance_miles != null ? `${c.distance_miles} mi` : null].filter(Boolean).join(" · ")}
      </div>
    </button>
  );

  return (
    <div className="flex gap-1">
      <Input
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-8 text-xs flex-1"
        data-testid={testId}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 px-2 shrink-0 text-[11px]" data-testid={`${testId}-picker`}>
            <Landmark className="w-3 h-3 mr-1 text-slate-500" />Change
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-2.5 z-[70]" align="end">
          <div className="space-y-2">
            {(options || []).length > 0 && (
              <div>
                <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Nearest to debtor (bankruptcy)</div>
                <div className="space-y-0.5 max-h-36 overflow-y-auto">
                  {options.map((c, i) => <CourtRow key={i} c={c} idx={i} prefix="nearby" />)}
                </div>
              </div>
            )}
            <div>
              <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Search courts by postcode</div>
              <div className="flex gap-1">
                <Input
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="e.g. SW1A 2AA"
                  className="h-7 text-xs"
                  data-testid={`${testId}-search-term`}
                />
                <Button size="sm" className="h-7 px-2" onClick={search} disabled={searching} data-testid={`${testId}-search-btn`}>
                  {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                </Button>
              </div>
              {error && <div className="text-[10px] text-amber-700 mt-1">{error}</div>}
              {results && results.length > 0 && (
                <div className="space-y-0.5 max-h-36 overflow-y-auto mt-1">
                  {results.map((c, i) => <CourtRow key={i} c={c} idx={i} prefix="result" />)}
                </div>
              )}
              {results && results.length === 0 && !error && (
                <div className="text-[10px] text-muted-foreground mt-1">No courts found for that postcode.</div>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground border-t pt-1.5">
              You can also type the court name directly into the field.
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ─── Default fee — % of principal (default 10%) or fixed £, toggleable ──────
function DefaultFeeField({ value, onChange, principal, testId }) {
  const [{ mode, pct }, setSt] = useState(() => {
    const v = parseMoney(value);
    if (v != null && principal > 0 && Math.abs(v - principal * 0.10) > 0.05) {
      return { mode: "fixed", pct: "10" };
    }
    return { mode: principal > 0 ? "pct" : "fixed", pct: "10" };
  });
  const applyPct = (p) => {
    setSt(s => ({ ...s, pct: p }));
    const f = parseFloat(p);
    if (Number.isFinite(f) && principal > 0) onChange(fmtMoney((principal * f) / 100));
  };
  const setMode = (m) => {
    setSt(s => ({ ...s, mode: m }));
    if (m === "pct") applyPct(pct);
  };
  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        <div className="flex rounded border overflow-hidden shrink-0">
          <button type="button" onClick={() => setMode("pct")} disabled={!principal}
            className={cn("px-2 h-8 text-[10px] font-medium", mode === "pct" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50", !principal && "opacity-40")}
            data-testid={`${testId}-mode-pct`}>% of loan</button>
          <button type="button" onClick={() => setMode("fixed")}
            className={cn("px-2 h-8 text-[10px] font-medium border-l", mode === "fixed" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}
            data-testid={`${testId}-mode-fixed`}>Fixed {cur()}</button>
        </div>
        {mode === "pct" ? (
          <>
            <Input type="number" min="0" step="0.5" value={pct} onChange={(e) => applyPct(e.target.value)}
              className="h-8 text-xs w-16 text-center" data-testid={`${testId}-pct`} />
            <span className="text-[11px] text-muted-foreground self-center shrink-0">% =</span>
            <Input value={value ?? ""} readOnly className="h-8 text-xs flex-1 bg-slate-50" data-testid={testId} />
          </>
        ) : (
          <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="e.g. 450.00"
            className="h-8 text-xs flex-1" data-testid={testId} />
        )}
      </div>
      {mode === "pct" && principal > 0 && (
        <div className="text-[10px] text-muted-foreground">{pct || 0}% of the loan principal ({cur()}{Number(principal).toLocaleString("en-GB")})</div>
      )}
    </div>
  );
}

// ─── Main wizard ────────────────────────────────────────────────────────────
export function GenerateLegalDocDialog({ kind, caseRow, onClose, presetFields, onGenerated }) {
  const [loading, setLoading]       = useState(true);
  const [loadErr, setLoadErr]       = useState(null);
  const [meta, setMeta]             = useState(null);
  const [fields, setFields]         = useState({});
  const [recipients, setRecipients] = useState([]);
  const [insolvencyOverride, setInsolvencyOverride] = useState(false);
  const [addressBook, setAddressBook] = useState([]);
  const isStatDemand = kind === "sd2" || kind === "sd_scot";
  const [logEvent, setLogEvent]     = useState(!isStatDemand);
  const [calDate, setCalDate]       = useState("");
  const [calDateTouched, setCalDateTouched] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [results, setResults]       = useState(null);
  const [stepIdx, setStepIdx]       = useState(0);
  const [packFor, setPackFor]       = useState(null);
  const [minimised, setMinimised]   = useState(false);
  const [aiBusy, setAiBusy]         = useState(false);
  const [aiResult, setAiResult]     = useState(null);
  const [aiSources, setAiSources]   = useState({});
  const [courtEditing, setCourtEditing] = useState(false);
  const [pgBusy, setPgBusy]         = useState(false);
  const [gate, setGate]             = useState(null);   // G3 checklist when SD2 generation is blocked

  const cfg = KIND_CONFIG[kind] || {};

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await getLegalDocPrefill(caseRow.case_id, kind);
        if (!alive) return;
        setMeta(r);
        setFields({ ...(r.fields || {}), ...Object.fromEntries(Object.entries(presetFields || {}).filter(([, v]) => v != null && v !== "")) });
        setRecipients((r.recipients || []).map(x => ({ ...x, checked: !x.insolvent })));
      } catch (e) {
        if (alive) setLoadErr(e?.response?.data?.detail || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    getLegalDocAddressBook(caseRow.case_id)
      .then(r => { if (alive) setAddressBook(r.candidates || []); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, caseRow.case_id]);

  // Steps derived from field groups (+ recipients live in "party")
  const defs = meta?.field_defs || [];
  const stepNames = { ...STEP_NAMES, ...(meta?.steps || {}) };
  const groups = STEP_ORDER.filter(
    g => defs.some(fd => fd.step === g) || (g === "party" && meta?.per_recipient)
  );
  const steps = [...groups, "review"];
  const stepId = steps[stepIdx];
  const isCourtDoc = meta?.group === "court";
  const { user: authUser } = useAuth();
  // Court letters: sign-off defaults to the logged-in user when the file has no handler
  useEffect(() => {
    if (!isCourtDoc || !authUser?.name) return;
    setFields(prev => (prev.HandlerName || "").trim() ? prev : { ...prev, HandlerName: authUser.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCourtDoc, authUser?.name, meta]);
  const isVisible = (fd) => {
    if (!fd.show_when) return true;
    return Object.entries(fd.show_when).every(([k, v]) => Boolean(fields[k]) === Boolean(v));
  };
  const pickDebtor = (r) => setFields(prev => ({ ...prev, DebtorName: r.name || "", ...(r.address ? { DebtorAddress: r.address } : {}) }));
  const pickCourt = (c) => setFields(prev => ({ ...prev, CourtName: c.name || "", ...("CourtAddress" in prev ? { CourtAddress: c.address || "" } : {}) }));

  // Calendar date — pre-set from the notice's expiry field, editable by the user
  const calField = meta?.calendar_date_field;
  useEffect(() => {
    if (calDateTouched) return;
    if (calField) {
      const p = parse((fields[calField] || "").trim(), "dd/MM/yyyy", new Date());
      setCalDate(isValid(p) ? format(p, "yyyy-MM-dd") : "");
    } else {
      setCalDate(format(new Date(), "yyyy-MM-dd"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, calField, calDateTouched]);

  const setField = (k, v) => {
    setFields(prev => {
      const next = { ...prev, [k]: v };
      // auto-recalc dependent totals (chained twice for Overall → Total)
      const compute = meta?.compute || {};
      for (let pass = 0; pass < 2; pass++) {
        for (const [target, spec] of Object.entries(compute)) {
          if (target === k) continue;
          const inputs = [...(spec.add || []), ...(spec.subtract || [])];
          if (!inputs.includes(k) && !(pass === 1 && inputs.some(i => compute[i]))) continue;
          let total = 0, any = false;
          for (const key of spec.add || []) {
            const f = parseMoney(next[key]);
            if (f !== null) { total += f; any = true; }
          }
          for (const key of spec.subtract || []) {
            const f = parseMoney(next[key]);
            if (f !== null) { total -= f; any = true; }
          }
          if (any) next[target] = fmtMoney(total);
        }
      }
      return next;
    });
  };

  const pickCompany = async (c) => {
    const fill = cfg.companyFill || {};
    setFields(prev => {
      const next = { ...prev };
      for (const key of fill.name || []) next[key] = c.name;
      if (fill.number) next[fill.number] = c.number;
      return next;
    });
    if (fill.address || cfg.addressFill?.line) {
      try {
        const p = await getCompanyProfileForDocs(c.number);
        if (p.ok && p.registered_office_address) {
          setFields(prev => {
            const next = { ...prev };
            if (fill.address) next[fill.address] = p.registered_office_address;
            else applyAddressFill(next, { full: p.registered_office_address, line: p.registered_office_address, city: "", postcode: "" });
            return next;
          });
        }
      } catch { /* snippet already good enough */ }
    }
    toast.success(`Companies House: ${c.name} (#${c.number})`);
  };

  const applyAddressFill = (target, addr) => {
    const fill = cfg.addressFill || {};
    if (fill.full) target[fill.full] = addr.full || addr.line || "";
    if (fill.line) target[fill.line] = addr.line || addr.full || "";
    if (fill.city && addr.city) target[fill.city] = addr.city;
    if (fill.postcode && addr.postcode) target[fill.postcode] = addr.postcode;
    return target;
  };

  const pickAddress = (addr) => setFields(prev => applyAddressFill({ ...prev }, addr));

  const pickedRecipients = recipients.filter(r => r.checked);
  const missingAddresses = meta?.address_required
    ? pickedRecipients.filter(r => !(r.address || "").trim())
    : [];

  // Jurisdiction guard — SD (England) can't be served on a Scottish address
  // and vice-versa. Blocks Generate unless the override is ticked.
  const [jxOverride, setJxOverride] = useState(false);
  const jxIssues = isStatDemand
    ? pickedRecipients
        .map(r => ({ r, country: postcodeCountry(r.address) }))
        .filter(({ country }) =>
          (kind === "sd2" && country === "scotland") ||
          (kind === "sd_scot" && country === "england_wales"))
        .map(({ r, country }) => `${r.name} (${country === "scotland" ? "Scottish" : "England/Wales"} address)`)
    : [];

  const handleGenerate = async () => {
    if (meta?.per_recipient && pickedRecipients.length === 0) {
      toast.error("Select at least one recipient"); return;
    }
    if (missingAddresses.length > 0) {
      toast.error(`Service address required for: ${missingAddresses.map(r => r.name).join(", ")}`); return;
    }
    setGenerating(true);
    try {
      const res = await generateLegalDocs(caseRow.case_id, {
        kind, fields,
        recipients: meta?.per_recipient
          ? pickedRecipients.map(r => ({ name: r.name, first_name: r.first_name, address: r.address || null }))
          : undefined,
        log_calendar_event: logEvent,
        calendar_date: logEvent ? (calDate || undefined) : undefined,
        jurisdiction_override: jxOverride,
        insolvency_override: insolvencyOverride,
        created_by: "Ops",
        court_source: aiSources?.CourtName ?? "",
      });
      setResults(res);
      const okCount = (res.documents || []).filter(d => d.ok).length;
      if (res.ok) {
        toast.success(`${res.label} generated (${okCount} document${okCount === 1 ? "" : "s"}) — saved to Files`);
        window.dispatchEvent(new CustomEvent("portal:files-changed"));
        onGenerated?.(res);
      } else {
        toast.error("Document generation failed — see details");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gate) { setGate(d.gate.gate); toast.error(`Gate ${d.gate.gate} — ${d.gate.label}`, { description: `${d.gate.blocking.length} open item${d.gate.blocking.length === 1 ? "" : "s"} — see the checklist` }); }
      else toast.error(errorDetailMessage(e, e.message));
    } finally {
      setGenerating(false);
    }
  };

  const label = meta?.label || kind.toUpperCase();

  const handleAiFill = async () => {
    setAiBusy(true);
    try {
      const r = await aiFillLegalDoc(caseRow.case_id, { kind, fields });
      const filled = r.fields || {};
      setFields(prev => ({ ...prev, ...filled }));
      setAiSources(Object.fromEntries(Object.keys(filled).map(k => [k, "ai"])));
      setAiResult(r);
      const n = Object.keys(filled).length;
      toast.success(n ? `AI filled ${n} field${n === 1 ? "" : "s"} from ${(r.files_read || []).length} document(s) — please review each one` : "AI found nothing new on the file");
    } catch (e) {
      toast.error(errorDetailMessage(e, e.message));
    } finally { setAiBusy(false); }
  };
  const sourceOf = (key) => aiSources[key] || meta?.sources?.[key];
  const handleFindGuarantors = async () => {
    setPgBusy(true);
    try {
      const r = await discoverLegalGuarantors(caseRow.case_id);
      const added = r.added || [];
      const enriched = r.enriched || [];
      if (added.length || enriched.length) {
        const fresh = await getLegalDocPrefill(caseRow.case_id, kind);
        setRecipients((fresh.recipients || []).map(x => ({ ...x, checked: !x.insolvent })));
        if (isCourtDoc) {
          const g = (fresh.recipients || []).find(x => /guarantor/i.test(x.role || ""));
          if (g) pickDebtor(g);
        }
        toast.success([added.length ? `Added ${added.map(a => a.name).join(", ")} as Guarantor (PG)` : "", enriched.length ? `Filled address/contact for ${enriched.map(a => a.name).join(", ")}` : ""].filter(Boolean).join(" · "));
      } else {
        toast.info((r.files_read || []).length ? "No new guarantors found in the signed PGs on file" : "No signed PG documents found on the Files tab");
      }
    } catch (e) {
      toast.error(errorDetailMessage(e, e.message));
    } finally { setPgBusy(false); }
  };
  const labelOf = (key) => defs.find(d => d.key === key)?.label || key;

  const renderField = (fd) => {
    // Court letters: the address is shown (and changed) inside the court box above
    if (isCourtDoc && fd.key === "CourtAddress" && (fields.CourtName || "").trim()) return null;
    const testId = `legal-doc-field-${fd.key}`;
    const commonLabel = (
      <label className="text-[11px] font-medium text-slate-700 flex items-center">
        {fd.label}<SourceBadge source={sourceOf(fd.key)} />
        {aiResult?.evidence?.[fd.key] && <span className="ml-1 text-[9px] text-amber-700 font-normal truncate max-w-[220px]" title={aiResult.evidence[fd.key]}>· {aiResult.evidence[fd.key]}</span>}
      </label>
    );
    if (fd.type === "date") {
      return (
        <div key={fd.key}>
          {commonLabel}
          <DateField value={fields[fd.key]} onChange={(v) => setField(fd.key, v)} placeholder={fd.placeholder} testId={testId} />
        </div>
      );
    }
    if (fd.type === "checkbox") {
      return (
        <label key={fd.key} className="col-span-2 flex items-start gap-2 text-xs border rounded p-2 bg-slate-50 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={Boolean(fields[fd.key])}
            onChange={(e) => setField(fd.key, e.target.checked)} data-testid={testId} />
          <span>
            <span className="font-medium text-slate-700">{fd.label}</span>
            {fd.placeholder && <span className="block text-[10px] text-muted-foreground">{fd.placeholder}</span>}
          </span>
        </label>
      );
    }
    if (fd.type === "time") {
      return (
        <div key={fd.key}>
          {commonLabel}
          <Input type="time" value={fields[fd.key] ?? ""} onChange={(e) => setField(fd.key, e.target.value)}
            className="h-8 text-xs" data-testid={testId} />
        </div>
      );
    }
    if (fd.type === "select") {
      return (
        <div key={fd.key}>
          {commonLabel}
          <select value={fields[fd.key] ?? ""} onChange={(e) => setField(fd.key, e.target.value)}
            className="h-8 text-xs border rounded px-2 w-full bg-white" data-testid={testId}>
            {(fd.options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }
    if (fd.debtor_picker) {
      return (
        <div key={fd.key} className="col-span-2">
          {commonLabel}
          <div className="flex gap-1">
            <Input value={fields[fd.key] ?? ""} onChange={(e) => setField(fd.key, e.target.value)}
              placeholder={fd.placeholder} className="h-8 text-xs flex-1" data-testid={testId} />
            {recipients.length > 0 && (
              <select className="h-8 text-xs border rounded px-1 bg-white w-[45%] min-w-0 shrink-0 truncate" value=""
                onChange={(e) => { const r = recipients[Number(e.target.value)]; if (r) pickDebtor(r); }}
                data-testid={`${testId}-picker`}>
                <option value="">Use guarantor…</option>
                {recipients.map((r, i) => <option key={i} value={i}>{r.name} · {r.role}</option>)}
              </select>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
            <span className="min-w-0">The petition is against the individual — pick the guarantor from the file, or type a name.</span>
            <button type="button" className="text-violet-700 underline disabled:opacity-50 inline-flex items-center gap-1" onClick={handleFindGuarantors} disabled={pgBusy} data-testid="legal-doc-find-guarantors-btn">
              {pgBusy && <Loader2 className="w-3 h-3 animate-spin" />}{pgBusy ? "Reading signed PGs…" : "Find from signed PG"}
            </button>
          </div>
        </div>
      );
    }
    if (fd.type === "textarea") {
      return (
        <div key={fd.key} className="col-span-2">
          {commonLabel}
          <div className="flex gap-1 items-start">
            <Textarea
              value={fields[fd.key] ?? ""}
              onChange={(e) => setField(fd.key, e.target.value)}
              placeholder={fd.placeholder}
              className="text-xs min-h-[52px] flex-1"
              data-testid={testId}
            />
            {fd.address_finder && <AddressFinder addressBook={addressBook} onPick={pickAddress} testId={`${testId}-finder`} />}
          </div>
        </div>
      );
    }
    if (fd.company_search) {
      return (
        <div key={fd.key} className="col-span-2">
          {commonLabel}
          <CompanySearchField
            value={fields[fd.key]}
            onChange={(v) => setField(fd.key, v)}
            onPickCompany={pickCompany}
            placeholder={fd.placeholder}
            testId={testId}
          />
          <div className="text-[10px] text-muted-foreground mt-0.5">Type 3+ characters to search Companies House — picking fills the number & registered address too.</div>
        </div>
      );
    }
    if (fd.court_picker && isCourtDoc) {
      const has = (fields[fd.key] || "").trim();
      const src = sourceOf(fd.key);
      const hint = src === "picked" ? "Chosen by you — saved to the file" : src === "ai" ? "Found by AI in the documents on this file" : src === "case" ? "From the file's issuing court / hearing" : src === "computed" ? "Nearest bankruptcy court to the debtor's address" : "";
      return (
        <div key={fd.key} className="col-span-2 space-y-2">
          {commonLabel}
          <div className="flex items-start gap-3 border rounded-md p-3 bg-violet-50/40" data-testid={`${testId}-display`}>
            <Landmark className="w-5 h-5 text-violet-600 mt-0.5 shrink-0" />
            <div className="text-sm flex-1 min-w-0">
              {has ? (
                <>
                  <div className="font-medium leading-snug">{fields[fd.key]}</div>
                  {fields.CourtAddress !== undefined && <div className="text-xs text-muted-foreground leading-snug mt-0.5">{fields.CourtAddress || "— address not on file — press Edit court to pick it —"}</div>}
                  {hint && <div className="text-[11px] text-violet-700 mt-1">{hint}</div>}
                </>
              ) : (
                <div className="text-sm text-amber-700">No court found for this file yet — press Edit court to search by the debtor's postcode.</div>
              )}
            </div>
            {!courtEditing && (
              <Button size="sm" variant="outline" className="h-8 text-xs shrink-0" onClick={() => setCourtEditing(true)} data-testid={`${testId}-edit`}>
                <Pencil className="w-3.5 h-3.5 mr-1" />Edit court
              </Button>
            )}
          </div>
          {courtEditing && (
            <CourtEditor
              options={meta?.court_options}
              defaultTerm={ukPostcode(fields.DebtorAddress)}
              testId={testId}
              onCancel={() => setCourtEditing(false)}
              onPick={(c) => {
                pickCourt(c);
                setAiSources(prev => ({ ...prev, [fd.key]: "picked", CourtAddress: "picked" }));
                setCourtEditing(false);
                setLegalChosenCourt(caseRow.case_id, c.name, c.address)
                  .then(() => toast.success(`${c.name} saved as this file's court — later letters will open with it`))
                  .catch(() => {});
              }}
            />
          )}
          <input type="hidden" value={fields[fd.key] ?? ""} readOnly data-testid={testId} />
        </div>
      );
    }
    if (fd.court_picker) {
      return (
        <div key={fd.key} className="col-span-2">
          {commonLabel}
          <CourtPickerField
            defaultTerm={isCourtDoc ? ukPostcode(fields.DebtorAddress) : ""}
            autoOpen={isCourtDoc && courtEditing}
            value={fields[fd.key]}
            onChange={(v) => setField(fd.key, v)}
            options={meta?.court_options}
            placeholder={fd.placeholder}
            testId={testId}
            onPickCourt={isCourtDoc ? pickCourt : undefined}
          />
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {isCourtDoc ? "Search by postcode (the debtor's postcode is pre-set) or type the court name — picking fills the address too." : "Auto-found from the debtor's postcode — edit the text or pick a different court if it's wrong."}
          </div>
        </div>
      );
    }
    if (fd.key === "LoanCustomField27220" && kind === "pdn") {
      return (
        <div key={fd.key}>
          {commonLabel}
          <DefaultFeeField
            value={fields[fd.key]}
            onChange={(v) => setField(fd.key, v)}
            principal={meta?.principal}
            testId={testId}
          />
        </div>
      );
    }
    return (
      <div key={fd.key}>
        {commonLabel}
        <div className="flex gap-1">
          <Input
            value={fields[fd.key] ?? ""}
            onChange={(e) => setField(fd.key, e.target.value)}
            placeholder={fd.placeholder}
            className="h-8 text-xs flex-1"
            data-testid={testId}
          />
          {fd.address_finder && <AddressFinder addressBook={addressBook} onPick={pickAddress} testId={`${testId}-finder`} />}
        </div>
      </div>
    );
  };

  const renderRecipients = () => (
    <div className="border rounded p-2.5 bg-slate-50">
      <div className="text-xs font-medium text-slate-700 mb-1.5">
        Recipients — one letter per person ({pickedRecipients.length} selected)
        {meta?.address_required && <span className="text-[10px] text-rose-600 font-normal ml-1">· service address required</span>}
      </div>
      {recipients.some(r => r.insolvent && r.checked) && (
        <label className="flex items-center gap-2 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1.5" data-testid="legal-doc-insolvency-override">
          <input type="checkbox" checked={insolvencyOverride} onChange={e => setInsolvencyOverride(e.target.checked)} />
          A guarantor with a logged bankruptcy / IVA is ticked — I confirm this letter may still be served on them (override)
        </label>
      )}
      {recipients.length === 0 ? (
        <div className="text-[11px] text-muted-foreground">No guarantors found — add one below.</div>
      ) : (
        <div className="space-y-1.5">
          {recipients.map((r, i) => (
            <div key={i} className="flex items-start gap-2 text-xs" data-testid={`legal-doc-recipient-${i}`}>
              <input
                type="checkbox" className="mt-2"
                checked={r.checked}
                onChange={e => setRecipients(prev => prev.map((x, j) => j === i ? { ...x, checked: e.target.checked } : x))}
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <Input
                    value={r.name}
                    onChange={e => {
                      const name = e.target.value;
                      setRecipients(prev => prev.map((x, j) => j === i ? { ...x, name, first_name: name.trim().split(" ")[0] || "" } : x));
                    }}
                    placeholder="Full name, e.g. John Smith"
                    className="h-7 text-xs flex-1"
                  />
                  <span className="text-[10px] text-muted-foreground shrink-0">{r.role}</span>
                  {r.insolvent && <span className="text-[10px] font-semibold text-red-700 shrink-0" data-testid={`legal-doc-recipient-insolvent-${i}`}>⚠ bankrupt / IVA logged — skipped</span>}
                </div>
                <div className="flex gap-1">
                  <Input
                    value={r.address || ""}
                    onChange={e => setRecipients(prev => prev.map((x, j) => j === i ? { ...x, address: e.target.value } : x))}
                    placeholder={meta?.address_required ? "Service address (required)" : "Service address (blank = company address)"}
                    className={cn("h-7 text-[11px] flex-1", meta?.address_required && !(r.address || "").trim() && r.checked && "border-rose-300")}
                  />
                  <AddressFinder
                    addressBook={addressBook}
                    onPick={(addr) => setRecipients(prev => prev.map((x, j) => j === i ? { ...x, address: addr.full || addr.line || "" } : x))}
                    testId={`legal-doc-recipient-${i}-finder`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Button
        size="sm" variant="ghost" className="h-6 text-[11px] mt-1.5 px-2"
        onClick={() => setRecipients(prev => [...prev, { name: "", first_name: "", address: "", role: "Manual", checked: true }])}
        data-testid="legal-doc-add-recipient"
      >
        <Plus className="w-3 h-3 mr-1" />Add recipient
      </Button>
    </div>
  );

  const renderReview = () => (
    <div className="space-y-3">
      <div className="border rounded p-3 bg-slate-50 space-y-1.5" data-testid="legal-doc-review">
        {defs.filter(isVisible).map(fd => (
          <div key={fd.key} className="flex justify-between gap-3 text-xs">
            <span className="text-muted-foreground shrink-0">{fd.label}</span>
            <span className={cn("font-medium text-right", fd.type !== "checkbox" && !(fields[fd.key] ?? "").toString().trim() && "text-amber-600")}>
              {fd.type === "checkbox" ? (fields[fd.key] ? "Yes" : "No") : ((fields[fd.key] ?? "").toString().trim() || "— blank —")}
            </span>
          </div>
        ))}
        {meta?.per_recipient && (
          <div className="pt-1.5 border-t mt-1.5">
            <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1">Recipients ({pickedRecipients.length})</div>
            {pickedRecipients.map((r, i) => (
              <div key={i} className="text-xs">
                <span className="font-medium">{r.name}</span>
                <span className="text-muted-foreground"> — {(r.address || "").trim() || "company address"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {missingAddresses.length > 0 && (
        <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
          Service address missing for: {missingAddresses.map(r => r.name).join(", ")} — go back to step 1.
        </div>
      )}
      {jxIssues.length > 0 && (
        <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-300 rounded p-2.5 space-y-1.5" data-testid="jurisdiction-blocker">
          <div className="font-semibold">
            ⚠ Jurisdiction mismatch — a {kind === "sd2" ? "Statutory Demand (England) can't be served on a Scottish address" : "Statutory Demand (Scotland) can't be served on an England/Wales address"}:
          </div>
          <div>{jxIssues.join("; ")}</div>
          <div>Use the {kind === "sd2" ? "Scottish" : "English"} statutory demand instead, or override below.</div>
          <label className="flex items-center gap-2 cursor-pointer font-medium">
            <input type="checkbox" checked={jxOverride} onChange={e => setJxOverride(e.target.checked)} data-testid="jurisdiction-override" />
            I understand — generate anyway
          </label>
        </div>
      )}
      {meta?.calendar_event !== null && <div className="text-xs text-slate-700 border rounded p-2 bg-blue-50/50 space-y-1.5" data-testid="legal-doc-calendar-block">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={logEvent} onChange={e => setLogEvent(e.target.checked)} data-testid="legal-doc-log-event" />
          <span>
            <span className="font-medium">{isStatDemand ? "Log on Legal Calendar" : kind === "bp_cover" ? "Log \"Petition Filed\" on the Legal Calendar (auto-diaries the 14-day court chase)" : `Log "${label} expires" on the calendar`}</span>
            {!isStatDemand && meta?.on_collections_calendar && (
              <span className="text-muted-foreground"> — shows on the Legal & Collections calendars</span>
            )}
            {isStatDemand && <span className="text-muted-foreground"> — records "{label} served" and auto-schedules the follow-ups</span>}
          </span>
        </label>
        {logEvent && (
          <div className="flex items-center gap-2 pl-6">
            <span className="text-[11px] text-muted-foreground shrink-0">{isStatDemand ? "Served date" : "Expiry date"}</span>
            <input
              type="date" value={calDate}
              onChange={(e) => { setCalDate(e.target.value); setCalDateTouched(true); }}
              className="h-7 text-xs border rounded px-1.5 bg-white"
              data-testid="legal-doc-calendar-date"
            />
            {!isStatDemand && calField && !calDateTouched && (
              <span className="text-[10px] text-muted-foreground">pre-set to the notice's expiry date</span>
            )}
          </div>
        )}
        {isStatDemand && <span className="block text-[10px] text-amber-700 pl-6">Recommended: leave off — log "Served" once the process server confirms service. You'll diary a chase event when sending the pack.</span>}
      </div>}
    </div>
  );

  const renderResults = () => (
    <div className="space-y-3">
      {(results.documents || []).map((d, i) => (
        <div key={i} className={cn("border rounded p-2.5 text-xs flex items-center gap-2", d.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200")} data-testid={`legal-doc-result-${i}`}>
          {d.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{d.filename || d.recipient || label}</div>
            {d.recipient && <div className="text-[10px] text-muted-foreground">To: {d.recipient}</div>}
            {!d.ok && <div className="text-[10px] text-red-700">{d.error}</div>}
            {d.ok && !d.onedrive_saved && <div className="text-[10px] text-amber-700">Generated but not saved to OneDrive — use the download link now (it expires)</div>}
            {d.ok && d.docx_filename && (d.docx_web_url || d.docx_file_url) && !d.docx_saved && <div className="text-[10px] text-amber-700">Word copy not saved to OneDrive — use the Word link now (it expires)</div>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(d.web_url || d.file_url) && (
              <a href={d.web_url || d.file_url} target="_blank" rel="noreferrer" className="text-blue-700 underline" data-testid={`legal-doc-open-pdf-${i}`}>Open PDF</a>
            )}
            {(d.docx_web_url || d.docx_file_url) && (
              <a href={d.docx_web_url || d.docx_file_url} target="_blank" rel="noreferrer" className="text-blue-700 underline" data-testid={`legal-doc-open-word-${i}`}>Download Word</a>
            )}
          </div>
        </div>
      ))}
      {results.calendar?.created > 0 && (
        <div className="text-xs text-slate-600 bg-slate-50 border rounded p-2">
          📅 Logged on Legal Calendar: {(results.calendar.events || []).map(e => `${e.type} (${ukDate(e.date)})`).join(" · ")}
        </div>
      )}
      {(results.warnings || []).map((w, i) => (
        <div key={i} className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">⚠ {w}</div>
      ))}
      {isStatDemand && results.ok && (
        <Button
          className="w-full"
          onClick={() => setPackFor({
            fileIds: (results.documents || []).filter(d => d.ok && d.file_id).map(d => `od:${d.file_id}`),
            debtor: pickedRecipients[0]?.name || "",
            address: pickedRecipients[0]?.address || "",
          })}
          data-testid="build-service-pack-btn"
        >
          Build service pack → process server
        </Button>
      )}
    </div>
  );

  if (minimised) {
    return (
      <button
        type="button"
        onClick={() => setMinimised(false)}
        className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 rounded-full border border-rose-300 bg-white shadow-lg px-4 py-2 text-xs font-medium text-rose-800 hover:bg-rose-50"
        data-testid="legal-doc-minimised-pill"
        title="Resume the document wizard"
      >
        <FileSignature className="w-4 h-4 text-rose-600" />
        {label} — {results ? "done" : `step ${stepIdx + 1}/${steps.length}`} · click to resume
      </button>
    );
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" aria-describedby={undefined} data-testid="legal-doc-dialog">
        <DialogHeader className="min-w-0">
          <DialogTitle className="flex items-center gap-2 pr-8 min-w-0 w-full">
            <FileSignature className="w-4 h-4 text-rose-600 shrink-0" />
            <span className="flex-1 min-w-0 truncate" title={`${label} — ${caseRow.customer_name}`}>{label} — {caseRow.customer_name}</span>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs font-normal text-slate-600 shrink-0" onClick={() => setMinimised(true)}
              title="Minimise — look something up on the file and come back; nothing is lost" data-testid="legal-doc-minimise-btn">
              <Minus className="w-3.5 h-3.5 mr-1" />Minimise
            </Button>
          </DialogTitle>
          {!results && !loading && !loadErr && (
            <div className="pt-1">
              <div className="text-xs text-muted-foreground mb-1.5" data-testid="legal-doc-step-indicator">
                Step {stepIdx + 1} of {steps.length} — {stepNames[stepId]}
              </div>
              <div className="flex gap-1.5">
                {steps.map((s, i) => (
                  <div key={s} className={cn("flex-1 h-1.5 rounded-full transition-colors", i <= stepIdx ? "bg-rose-500" : "bg-slate-200")} />
                ))}
              </div>
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Pre-filling from LoanDisk{kind !== "pdn" ? " + Companies House" : ""}{meta?.group === "court" ? " + the legal file" : ""}…
          </div>
        ) : loadErr ? (
          <div className="py-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{loadErr}</div>
        ) : results ? renderResults() : (
          <div className="space-y-3">
            {meta && meta.template_ready === false && (
              <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded p-2" data-testid="legal-doc-template-missing">
                ⚠ {meta.template_missing_hint || "The DocuPilot template for this letter hasn't been uploaded yet — you can pre-fill, but Generate will be refused until it is."}
              </div>
            )}
            {(meta?.loan_errors || []).length > 0 && stepIdx === 0 && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠ Some LoanDisk data couldn't be fetched — check the pre-filled values carefully.
              </div>
            )}
            {stepIdx === 0 && (
              <div className="border rounded-md p-4 bg-amber-50/60 border-amber-200 space-y-3" data-testid="legal-doc-ai-panel">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                    <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />Fill with AI
                  </div>
                  <Button size="sm" className="h-8 text-xs bg-amber-600 hover:bg-amber-700 shrink-0" onClick={handleAiFill} disabled={aiBusy} data-testid="legal-doc-ai-fill-btn">
                    {aiBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                    {aiBusy ? "Reading the file…" : aiResult ? "Run again" : "Fill with AI"}
                  </Button>
                </div>
                <p className="text-xs leading-relaxed text-slate-700">
                  Reads the documents on this file ({isCourtDoc ? "sealed petitions, certificates of service, orders, emails" : "agreements, signed PGs, notices, emails"}), the legal calendar and the timeline, then proposes values for the fields. Today's date and the sign-off are never taken from old documents — you review every field before generating.
                </p>
                {aiResult && (
                  <div className="text-xs leading-relaxed text-slate-800 space-y-1.5 border-t border-amber-200 pt-3" data-testid="legal-doc-ai-summary">
                    <div>Read <b>{(aiResult.files_read || []).length}</b> document(s){(aiResult.files_skipped || []).length ? `, skipped ${aiResult.files_skipped.length} unreadable` : ""} · <b>{Object.keys(aiResult.fields || {}).length}</b> field(s) filled — look for the <span className="text-amber-700 font-medium">AI ✨</span> badge.</div>
                    {aiResult.case_number_saved && <div className="text-violet-800">Court case number saved to the legal file — later letters will pre-fill it.</div>}
                    {(aiResult.unsure || []).length > 0 && <div className="text-amber-900"><b>Double-check:</b> {aiResult.unsure.map(labelOf).join(", ")}</div>}
                    {(aiResult.notes || []).length > 0 && (
                      <ul className="list-disc pl-4 space-y-1 text-amber-950">
                        {aiResult.notes.map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
            {stepId === "review" ? renderReview() : (
              <>
                {stepId === "amount" && (
                  <div className="text-[11px] text-muted-foreground">
                    Values verified from LoanDisk where badged. Enter numbers only — the {cur()} symbol is handled automatically. Totals recalculate as you edit.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2.5 min-w-0 [&>*]:min-w-0">
                  {defs.filter(fd => fd.step === stepId && isVisible(fd)).map(renderField)}
                </div>
                {stepId === "party" && meta?.per_recipient && renderRecipients()}
              </>
            )}
          </div>
        )}

        <DialogFooter className="gap-1">
          <Button variant="ghost" onClick={onClose} data-testid="legal-doc-close">{results ? "Done" : "Cancel"}</Button>
          {!results && !loading && !loadErr && (
            <>
              {stepIdx > 0 && (
                <Button variant="outline" onClick={() => setStepIdx(i => i - 1)} data-testid="legal-doc-back-btn">
                  <ChevronLeft className="w-3.5 h-3.5 mr-1" />Back
                </Button>
              )}
              {stepId !== "review" ? (
                <Button onClick={() => setStepIdx(i => i + 1)} data-testid="legal-doc-next-btn">
                  Next<ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleGenerate} disabled={generating || missingAddresses.length > 0 || (jxIssues.length > 0 && !jxOverride)} data-testid="legal-doc-generate-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                  {generating ? "Generating…" : `Generate ${meta?.per_recipient ? `(${pickedRecipients.length})` : ""}`}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
      {packFor && (
        <ServicePackDialog
          caseRow={caseRow}
          kind={kind}
          preselectFileIds={packFor.fileIds}
          debtorName={packFor.debtor}
          serviceAddress={packFor.address}
          onClose={() => setPackFor(null)}
        />
      )}
      {gate && (
        <GateChecklistDialog caseRow={caseRow} gate={gate} proceedLabel={`Continue — generate ${label}`}
          onClose={() => setGate(null)} onPassed={() => { setGate(null); handleGenerate(); }} />
      )}
    </Dialog>
  );
}
